import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaMusic } from "react-icons/fa";
import { HiOutlinePlay } from "react-icons/hi2";
import { useAuth } from "../context/AuthContext";
import AdminSongs from "./Organization/Songs/Songs";
import SongDatabase from "./User/SongDatabase";
import SongViewer from "./User/SongViewer";

type SongsTab = "library" | "chords";

/** Songs hub: library + chord viewer. */
const SongLibrary: React.FC = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo((): SongsTab => {
    return searchParams.get("tab") === "chords" ? "chords" : "library";
  }, [searchParams]);

  const setTab = (next: SongsTab) => {
    if (next === "library") {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: next }, { replace: true });
    }
  };

  const tabs: { id: SongsTab; labelKey: string; icon: typeof FaMusic }[] = [
    { id: "library", labelKey: "songsLibrary", icon: FaMusic },
    { id: "chords", labelKey: "songViewer", icon: HiOutlinePlay },
  ];

  return (
    <div>
      <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 mb-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur-md">
        <div className="mx-auto max-w-6xl py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
                {t("songs")}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {tab === "library"
                  ? t("songsLibrarySubtitle")
                  : t("songViewerSubtitle")}
              </p>
            </div>
            <div
              className="inline-flex w-full sm:w-auto p-1 rounded-2xl bg-gray-100/80 dark:bg-gray-800/80 border border-gray-200/70 dark:border-gray-700/70"
              role="tablist"
              aria-label={t("songs")}
            >
              {tabs.map(({ id, labelKey, icon: Icon }) => {
                const active = tab === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(id)}
                    className={`flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      active
                        ? "bg-white dark:bg-gray-700 text-[#5932EA] shadow-sm border border-gray-200/80 dark:border-gray-600/80"
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                    }`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{t(labelKey)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div>
        {tab === "library" && (isAdmin ? <AdminSongs /> : <SongDatabase />)}
        {tab === "chords" && <SongViewer embedded />}
      </div>
    </div>
  );
};

export default SongLibrary;
